import {
  createContext,
  Dispatch,
  SetStateAction,
  use,
  useEffect,
  useState
} from 'react';
import { useStep } from 'usehooks-ts';

// Types
import { WorkshopFormValues } from './types';

interface Helpers {
  goToNextStep: () => void;
  goToPrevStep: () => void;
  reset: () => void;
  canGoToNextStep: boolean;
  canGoToPrevStep: boolean;
  setStep: Dispatch<SetStateAction<number>>;
}

interface WorkshopStepContextProps {
  currentStep: number;
  helpers: Helpers;
  isEditMode: boolean;
  focusField: keyof WorkshopFormValues | null;
  setFocusField: Dispatch<SetStateAction<keyof WorkshopFormValues | null>>;
}

const WorkshopStepContext = createContext<WorkshopStepContextProps | null>(
  null
);

export const STEPS = {
  DETAILS: 1,
  SCHEDULE: 2,
  NOTES: 3,
  SUMMARY: 4
};

export const TOTAL_STEPS = Object.keys(STEPS).length;

export function WorkshopStepProvider({
  children,
  isEditMode = false
}: {
  children: React.ReactNode;
  isEditMode?: boolean;
}) {
  const [currentStep, helpers] = useStep(TOTAL_STEPS);
  const [focusField, setFocusField] = useState<keyof WorkshopFormValues | null>(
    null
  );

  useEffect(() => {
    if (isEditMode) {
      helpers.setStep(STEPS.SUMMARY);
    }
  }, [isEditMode]);

  return (
    <WorkshopStepContext
      value={{ currentStep, helpers, isEditMode, focusField, setFocusField }}
    >
      {children}
    </WorkshopStepContext>
  );
}

export function useWorkshopStepContext() {
  const context = use(WorkshopStepContext);
  if (!context) {
    throw new Error(
      'useWorkshopStepContext must be used within a WorkshopStepProvider'
    );
  }

  return context;
}
